/**
 * Storage availability, durability and quota. NFR-05, NFR-07, R-04.
 *
 * R-04 — browser storage eviction destroying a user's history — is rated high
 * probability AND high impact, and it is worst on iOS where storage for a
 * non-installed site can be evicted after roughly a week of non-use. The app
 * cannot prevent that. What it can do is know when it has happened, say so
 * plainly, and push export before it does.
 *
 * The other case this covers is IndexedDB being unavailable at all — Firefox
 * private windows historically refuse it, and some embedded webviews do too.
 * A local-first app whose storage is missing must say that in words rather than
 * render an empty calendar that silently forgets everything.
 */
import { openDb } from './db'

export type StorageAvailability = 'ok' | 'unavailable' | 'error'

export interface StorageHealth {
  availability: StorageAvailability
  /** True when the browser has agreed not to evict this origin under pressure. */
  persistent: boolean
  /** Bytes used and available, when the browser reports them. */
  usage?: number
  quota?: number
  /** True when a previous run stored data and this one found none. */
  possibleEviction: boolean
}

const EVIDENCE_KEY = 'thana_storage_evidence'

/**
 * A breadcrumb in localStorage recording that entries once existed.
 *
 * localStorage and IndexedDB are evicted together in practice, so this is not a
 * reliable detector on its own — but the case it DOES catch is the common one:
 * IndexedDB wiped while localStorage survived. When both are gone the app is
 * indistinguishable from a fresh install, which is a limitation to disclose
 * rather than paper over.
 */
export function recordStorageEvidence(entryCount: number): void {
  if (entryCount > 0) localStorage.setItem(EVIDENCE_KEY, String(entryCount))
}

export function clearStorageEvidence(): void {
  localStorage.removeItem(EVIDENCE_KEY)
}

export async function checkStorageHealth(entryCount: number): Promise<StorageHealth> {
  let availability: StorageAvailability = 'ok'
  try {
    await openDb()
  } catch {
    availability = typeof indexedDB === 'undefined' ? 'unavailable' : 'error'
  }

  let persistent = false
  let usage: number | undefined
  let quota: number | undefined

  if (typeof navigator !== 'undefined' && navigator.storage) {
    try {
      persistent = (await navigator.storage.persisted?.()) ?? false
      const estimate = await navigator.storage.estimate?.()
      usage = estimate?.usage
      quota = estimate?.quota
    } catch {
      // Storage manager unavailable or blocked. Not fatal — the app works
      // either way, it just cannot report how much room is left.
    }
  }

  const previously = Number(localStorage.getItem(EVIDENCE_KEY) ?? '0')
  const possibleEviction = availability === 'ok' && previously > 0 && entryCount === 0

  const health: StorageHealth = { availability, persistent, possibleEviction }
  if (usage !== undefined) health.usage = usage
  if (quota !== undefined) health.quota = quota
  return health
}

/** True when a write failed because the origin is out of room. */
export function isQuotaError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'QuotaExceededError' ||
      // Safari's legacy name for the same condition.
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  )
}
